import { useTranslation } from 'react-i18next';
import type { TaskDto } from '@monorepo/shared';
import type { TaskScopeTab } from '../../services/tasks.service';
import { TaskStatusBadge } from './TaskStatusBadge';
import { TaskActionCell } from './TaskActionCell';
import { TaskAllScopeActions } from './TaskAllScopeActions';
import { TaskDueDateCell } from './TaskDueDateCell';
import { formatCompletionDate } from './taskDisplay';

interface TaskRowProps {
  task: TaskDto;
  scope: TaskScopeTab;
  pendingReminderTaskId: string | null;
  onAction: (task: TaskDto) => void;
  onViewDocument: (task: TaskDto) => void;
  onSendReminder: (task: TaskDto) => void;
}

const getInitials = (firstName: string, lastName: string) =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const stackedCellClassName = 'flex items-center justify-between gap-3 px-4 lg:table-cell lg:py-3';

const mobileLabelClassName = 'text-xs font-medium uppercase text-gray-500 lg:hidden';

export const TaskRow = ({
  task,
  scope,
  pendingReminderTaskId,
  onAction,
  onViewDocument,
  onSendReminder,
}: TaskRowProps) => {
  const { t } = useTranslation();
  const completedOn = formatCompletionDate(task.completedAt);

  return (
    <tr className="flex flex-col gap-3 rounded-lg bg-white py-4 shadow-sm lg:table-row lg:py-0">
      <td className="px-4 lg:table-cell lg:py-3 lg:rounded-l-lg">
        <div className="font-medium text-gray-900">{t(task.titleKey)}</div>
        <div className="text-sm text-gray-500">{task.subtitle}</div>
      </td>

      {scope === 'all' && (
        <td className={stackedCellClassName}>
          <span className={mobileLabelClassName}>{t('tasks.table.columns.user')}</span>
          {task.user ? (
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#dee7ed] text-xs font-medium text-primary">
                {getInitials(task.user.firstName, task.user.lastName)}
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-900">
                  {task.user.firstName} {task.user.lastName}
                </div>
                <div className="truncate text-xs text-gray-500">{task.user.email}</div>
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
      )}

      <td className={stackedCellClassName}>
        <span className={mobileLabelClassName}>{t('tasks.table.columns.status')}</span>
        <div className="flex flex-col items-end gap-0.5 lg:items-start">
          <TaskStatusBadge status={task.status} />
          {completedOn && (
            <span className="text-xs text-gray-500">
              {t('tasks.completed_on', { date: completedOn })}
            </span>
          )}
        </div>
      </td>

      <td className={stackedCellClassName}>
        <span className={mobileLabelClassName}>{t('tasks.table.columns.due_date')}</span>
        <TaskDueDateCell task={task} />
      </td>

      <td className="px-4 lg:table-cell lg:py-3 lg:text-right lg:rounded-r-lg">
        {scope === 'all' ? (
          <TaskAllScopeActions
            task={task}
            isReminderPending={pendingReminderTaskId === task.id}
            onViewDocument={onViewDocument}
            onSendReminder={onSendReminder}
          />
        ) : (
          <TaskActionCell task={task} onAction={onAction} />
        )}
      </td>
    </tr>
  );
};
